/**
 * AI model registry admin page (PRD §5.2 C-08).
 *
 * Lists every trained classifier version in `model_registry`, shows
 * metadata, and lets ADMIN activate a specific version. The classifier
 * sidecar reads its active model from disk (ACTIVE_MODEL_PATH env var)
 * — the DB registry is the human-facing catalog + activation record.
 *
 * There is NO "upload model" form here because the model artifact is a
 * joblib file that lives on the VPS filesystem, not something uploaded
 * from the browser. New models are registered via `POST /api/v1/models`
 * from the train_baseline.py script after training completes.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AdminNav } from "@/components/AdminNav";
import { useState } from "react";

interface ModelEntry {
  id: string;
  modelId: string;
  version: string;
  trainedAt: string;
  datasetSize: number;
  accuracyMetrics: {
    accuracy: number;
    precision: Record<string, number>;
    recall: Record<string, number>;
    f1: Record<string, number>;
    confusionMatrix: number[][];
  };
  featureNames: string[];
  artifactUri: string;
  isActive: boolean;
  createdAt: string;
}

export function ModelsAdminPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isAdmin = user?.role === "ADMIN";
  const [activatingId, setActivatingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery<{ models: ModelEntry[] }>({
    queryKey: ["admin", "models"],
    queryFn: () => api<{ models: ModelEntry[] }>("/models"),
  });

  const activateMut = useMutation({
    mutationFn: (id: string) =>
      api<{ model: ModelEntry }>(`/models/${id}/activate`, { method: "PATCH" }),
    onMutate: (id) => setActivatingId(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "models"] });
      setActivatingId(null);
      setError(null);
    },
    onError: (err: unknown) => {
      setActivatingId(null);
      setError(err instanceof ApiError ? err.message : "activate failed");
    },
  });

  return (
    <div className="space-y-5" data-testid="models-admin-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <AdminNav />
        <span className="text-xs text-slate-500">
          {isAdmin ? "ADMIN — you can activate" : "Read-only (requires ADMIN)"}
        </span>
      </div>

      <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
        <h2 className="text-sm font-semibold text-slate-200 mb-3">
          Trained classifier models
        </h2>
        {error && (
          <div
            className="text-xs text-rose-400 mb-2"
            data-testid="admin-models-error"
          >
            {error}
          </div>
        )}
        {isLoading ? (
          <div className="text-xs text-slate-500 italic" data-testid="admin-models-loading">
            Loading…
          </div>
        ) : (data?.models ?? []).length === 0 ? (
          <div className="text-xs text-slate-500 italic" data-testid="admin-models-empty">
            No models registered yet. Train via
            <code className="mx-1 bg-slate-950 px-1 rounded">python scripts/train_baseline.py</code>
            and POST the resulting metrics.
          </div>
        ) : (
          <table className="w-full text-xs" data-testid="admin-models-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
                <th className="py-1 font-medium">model_id</th>
                <th className="py-1 font-medium">version</th>
                <th className="py-1 font-medium text-right">accuracy</th>
                <th className="py-1 font-medium text-right">dataset</th>
                <th className="py-1 font-medium">trained_at</th>
                <th className="py-1 font-medium">active</th>
                <th className="py-1 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {(data?.models ?? []).map((m) => (
                <tr
                  key={m.id}
                  className="border-b border-slate-900"
                  data-testid={`admin-model-row-${m.id}`}
                >
                  <td className="py-1.5 font-mono text-slate-300">{m.modelId}</td>
                  <td className="py-1.5 font-mono text-slate-300">{m.version}</td>
                  <td className="py-1.5 font-mono text-right">
                    <span
                      className={
                        m.accuracyMetrics.accuracy >= 0.75
                          ? "text-emerald-400"
                          : "text-rose-400"
                      }
                    >
                      {(m.accuracyMetrics.accuracy * 100).toFixed(1)}%
                    </span>
                  </td>
                  <td className="py-1.5 font-mono text-right text-slate-400">
                    {m.datasetSize.toLocaleString()}
                  </td>
                  <td className="py-1.5 text-slate-400">
                    {new Date(m.trainedAt).toLocaleString()}
                  </td>
                  <td className="py-1.5">
                    {m.isActive ? (
                      <span
                        className="inline-flex items-center gap-1 text-teal-300"
                        data-testid={`admin-model-${m.id}-active`}
                      >
                        <CheckCircle2 className="size-3" /> ACTIVE
                      </span>
                    ) : (
                      <span className="text-slate-600">—</span>
                    )}
                  </td>
                  <td className="py-1.5 text-right">
                    {!m.isActive && isAdmin && (
                      <button
                        type="button"
                        onClick={() => activateMut.mutate(m.id)}
                        disabled={activatingId === m.id}
                        data-testid={`admin-model-${m.id}-activate`}
                        className="text-[11px] border border-slate-700 hover:border-teal-500 rounded px-2 py-0.5 text-slate-300 hover:text-teal-300 disabled:opacity-50"
                      >
                        {activatingId === m.id ? "…" : "Activate"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      <p className="text-xs text-slate-500">
        Activating a model flips <code>is_active</code> in the DB; the classifier sidecar
        restart will pick it up. For immediate effect, restart{" "}
        <code>pm2 restart sentinel-classifier</code> after activation.
      </p>
    </div>
  );
}
